import { useCallback, useEffect, useRef, useState } from "react";

import { logError } from "../../../utils/appUtils";
import { shipWithCacheKey } from "../../../utils/shipWith";
import { fetchActiveGateCheckIn } from "./gateCheckInUtils";

// Same visibility-aware cadence as the Gate Queue page — a dispatch tab left
// open all afternoon should not poll while the tab is hidden.
const REFRESH_INTERVAL = 20 * 1000;

/**
 * Active gate check-in for one shipment, kept current while the form is open.
 * The same row is editable from the Gate Queue page, so without the poll the
 * dispatch tab would show whatever the status was when the form was opened.
 *
 * shipmentType is "in" | "out" — the shipmentKey prefix the driver API writes.
 *
 * A linked outbound load (LTL "ship with") shares one row with its siblings,
 * keyed under whichever load the driver searched: `groupIds` (the shipment's
 * `shipWithIds`) has every member's key read, and `checkInId` (the shipment's
 * own `gateCheckInId` lock) names the row outright and is read first. Both
 * are optional; a single-load form passes neither and reads as it always did.
 */
export const useActiveGateCheckIn = (
  shipmentType,
  shipmentId,
  groupIds = null,
  checkInId = null,
) => {
  const [checkIn, setCheckIn] = useState(null);
  // Attached by the consumer to its root node. The control lives on the
  // Dispatch FormTab, and react-admin keeps inactive tabs mounted but
  // display:none — no point polling for a control nobody can see. A
  // display:none ancestor makes offsetParent null; an unattached ref (no
  // consumer opt-in) polls normally.
  const containerRef = useRef(null);
  // The group is joined on its sorted id string, so a re-render handing over a
  // fresh array of the same ids does not restart the poll.
  const groupKey = shipWithCacheKey(groupIds);

  const refresh = useCallback(async () => {
    if (!shipmentId) return;
    try {
      setCheckIn(
        await fetchActiveGateCheckIn(
          shipmentType,
          shipmentId,
          groupKey ? groupKey.split(",") : undefined,
          checkInId || undefined,
        ),
      );
    } catch (err) {
      logError("useActiveGateCheckIn:fetch", err, false);
    }
  }, [shipmentType, shipmentId, groupKey, checkInId]);

  useEffect(() => {
    refresh();
    const intervalId = setInterval(() => {
      if (document.visibilityState === "hidden") return;
      if (containerRef.current && containerRef.current.offsetParent === null)
        return;
      refresh();
    }, REFRESH_INTERVAL);
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh]);

  // Our own write already returned the fresh row; merging it beats waiting out
  // the poll interval for the UI to agree with what was just saved.
  const applySaved = useCallback(
    (saved) => setCheckIn((current) => ({ ...current, ...saved })),
    [],
  );

  return { checkIn, refresh, applySaved, containerRef };
};
